<script setup lang="ts">
    import { reactive, computed, watch } from 'vue';
    import FormInput from '@shared/components/Form/FormInput.vue';
    import FormTextArea from '@shared/components/Form/FormTextArea.vue';
    import FormSelect from '@shared/components/Form/FormSelect.vue';
    import MultiSelect from '@shared/components/MultiSelect/MultiSelect.vue';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import {
        createConnection,
        updateConnection,
        type ConnectionSummary,
        type ConnectionSaveModel,
        type ConnectionType,
        type ConnectionProperty,
    } from './AccountConnections.access';
    import { fieldAssistants } from './fieldAssistants';

    interface EditableProperty extends ConnectionProperty {
        Value: string;
    }

    const props = defineProps<{
        connectionTypes: ConnectionType[];
        connection: ConnectionSummary | null;
    }>();

    const emit = defineEmits<{
        (ev: 'saved', connection: ConnectionSummary): void;
    }>();

    const state = reactive<{
        selectedType: ConnectionType | null;
        name: string;
        properties: EditableProperty[];
        saving: boolean;
    }>({
        selectedType: null,
        name: '',
        properties: [],
        saving: false,
    });

    function buildProperties(type: ConnectionType, data: Record<string, string>): EditableProperty[] {
        return type.Properties.map((property) => ({
            ...property,
            // A dropdown with no matching value still visually shows its first option (native <select> behavior) - default to it here too, so the saved Value matches what's on screen.
            Value: data[property.Name] || property.Options?.[0] || '',
        }));
    }

    function initFromConnection(): void {
        if (!props.connection) {
            state.selectedType = null;
            state.name = '';
            state.properties = [];
            return;
        }

        const type = props.connectionTypes.find((t) => t.Name === props.connection!.Type) ?? null;
        const data = props.connection.Data ? JSON.parse(props.connection.Data) : {};

        state.selectedType = type;
        state.name = props.connection.Name;
        state.properties = type ? buildProperties(type, data) : [];
    }

    watch([() => props.connection, () => props.connectionTypes], initFromConnection, { immediate: true });

    const saveEnabled = computed(() => !state.saving && state.properties.every((property) => !property.IsRequired || !!property.Value));

    // Handed to a field's assistant (see fieldAssistants.ts) so it can build
    // its own request to whatever backend it needs - e.g. Salesforce's needs
    // the auth fields to run live discovery before this connection is saved.
    // This form never inspects the values itself; it's opaque pass-through.
    const allFieldValues = computed(() => Object.fromEntries(state.properties.map((p) => [p.Name, p.Value])));

    function selectType(type: ConnectionType): void {
        state.selectedType = type;
        state.properties = buildProperties(type, {});
    }

    function selectTypeFromArray(selected: ConnectionType[]): void {
        const type = selected[0] ?? null;

        if (type) {
            selectType(type);
        } else {
            state.selectedType = null;
            state.properties = [];
        }
    }

    function propertiesToData(): string {
        const data: Record<string, string> = {};
        state.properties.forEach((property) => (data[property.Name] = property.Value));
        return JSON.stringify(data);
    }

    async function save(): Promise<void> {
        if (!state.selectedType || !saveEnabled.value) return;

        state.saving = true;

        try {
            const payload: ConnectionSaveModel = {
                Name: state.name,
                Type: state.selectedType.Name,
                Data: propertiesToData(),
            };

            const saved = props.connection ? await updateConnection(props.connection.ConnectionId, payload) : await createConnection(payload);
            emit('saved', saved);
            bsAlert();
        } catch {
            bsAlert({ type: AlertTypes.Danger });
        } finally {
            state.saving = false;
        }
    }

    defineExpose({ save, saveEnabled, saving: computed(() => state.saving), hasSelectedType: computed(() => !!state.selectedType) });
</script>

<template>
    <div>
        <div v-if="!connection" class="form-group">
            <MultiSelect
                :label="$Resources.CreateConnectionTypesLabel"
                :items="connectionTypes"
                displayProp="DisplayName"
                selectProp="Name"
                :maxSelectable="1"
                :selectedItems="state.selectedType ? [state.selectedType] : []"
                @update:selectedItems="selectTypeFromArray"></MultiSelect>
        </div>

        <template v-if="state.selectedType">
            <FormInput
                class="form-group"
                :label="$Resources.Name"
                :modelValue="state.name"
                @update:modelValue="(newVal) => (state.name = String(newVal ?? ''))"></FormInput>

            <div v-for="property in state.properties" :key="property.Name" class="form-group">
                <FormTextArea
                    v-if="property.UseTextArea"
                    :label="property.Name"
                    :required="property.IsRequired"
                    :modelValue="property.Value"
                    @update:modelValue="(newVal: string) => (property.Value = newVal ?? '')"></FormTextArea>

                <FormSelect
                    v-else-if="property.Options"
                    :label="property.Name"
                    :required="property.IsRequired"
                    :modelValue="property.Value"
                    :options="property.Options.map((option) => ({ label: option, value: option }))"
                    @update:modelValue="(newVal: string) => (property.Value = newVal ?? '')"></FormSelect>

                <FormInput
                    v-else
                    :label="property.Name"
                    :required="property.IsRequired"
                    :type="property.IsSecret ? 'password' : 'text'"
                    :modelValue="property.Value"
                    @update:modelValue="(newVal) => (property.Value = String(newVal ?? ''))"></FormInput>

                <!-- Generic extension point (see fieldAssistants.ts) - a field
                     declares AssistantKey server-side via [FieldAssistant] when
                     it has an interactive builder; this form has no idea what
                     that component does or which integration it's for. -->
                <component
                    :is="fieldAssistants[property.AssistantKey]"
                    v-if="property.AssistantKey && fieldAssistants[property.AssistantKey]"
                    :allFields="allFieldValues"
                    :modelValue="property.Value"
                    @update:modelValue="(newVal: string) => (property.Value = newVal)" />
            </div>

            <p v-if="connection">
                <i>ConnectionId: {{ connection.ConnectionId }}</i>
            </p>
        </template>
    </div>
</template>

